/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { colors, rebrandColors, mediumInter, semiBold } from '@/styles';
import { Button, type ButtonProps, LoadingDots } from '@/atoms';
import { DropDown, type DropDownProps } from '@/molecules';
import { getSmallestBreakpointForWidth } from '@/functions';
import type { StyledComponent } from '@/helpers';
import { Layout } from '@/constants/layouts';
import { VideoLibraryBreakpoints } from '@/constants';

type TeamFolder = {
  createdDate: string;
  emailCount: number;
  id: string;
  name: string;
  videoCount: number;
};

export type LabelsMenuProps = StyledComponent & {
  /** Provides default value for dropdown */
  defaultDropdownValue?: string;
  /** Determines disabled state of buttons */
  disableButtons?: boolean;
  /** Items to be rendered in dropdown */
  dropdownItems: DropDownProps['menuItems'];
  /** Callback function on dropdown item selection */
  handleDropdownItemChange: DropDownProps['onChange'];
  /** Callback function on folder dropdown item selection */
  handleFoldersChange?: DropDownProps['onChange'];
  /** Boolean loading state for folders for overlay */
  loadingTeamFolders?: boolean;
  /** Classnames for overlay */
  overlayClasses?: string;
  /** Callback function for previewing item */
  onItemPreview?: ButtonProps['onClick'];
  /** Callback function for selecting item */
  onItemSelect?: React.MouseEventHandler;
  /** called when a list item menu is opened */
  onMenuOpen?: DropDownProps['onOpen'];
  /** Determines color of buttons */
  themeColor?: ButtonProps['color'];
  /** Layout type to render */
  layout?: Layout;
  /** determine if the insert button is active */
  canInsert?: boolean;
  /** determine if the insert button is visible */
  showInsertButton?: boolean;
  /** Array of team folders for folder dropdown */
  teamFolders?: TeamFolder[] | null;
  /** Size indicator for parent component */
  containerSize?: keyof typeof VideoLibraryBreakpoints | 'tiny' | '';
  /** Use new branding */
  isRebrand?: boolean;
};

// required for default prop functions to pass coverage
const noOp = () => null;

/**
 * LabelsMenu component
 */
const LabelsMenu = ({
  canInsert = true,
  className = '',
  containerSize = '',
  defaultDropdownValue = 'All labels',
  disableButtons = false,
  dropdownItems,
  handleDropdownItemChange,
  handleFoldersChange = () => {},
  layout = 'default',
  loadingTeamFolders = false,
  onItemPreview = noOp,
  onItemSelect = noOp,
  onMenuOpen = noOp,
  overlayClasses = '',
  showInsertButton = true,
  teamFolders = null,
  themeColor = 'kermit',
  isRebrand = false,
}: LabelsMenuProps) => {
  // default to the containerSize prop, but calculate our size if containerSize is
  // an empty string for clients that won't pass this in...
  const menu = useRef<HTMLDivElement>(null);
  const [internalSize, setInternalSize] = useState(containerSize);

  const compactLayout = useMemo(() => layout === 'compact', [layout]);
  const smallDisplay = useMemo(
    () =>
      !compactLayout &&
      (internalSize === 'small' ||
        internalSize === 'xsmall' ||
        internalSize === 'chrome' ||
        internalSize === 'mobile' ||
        internalSize === 'tiny'),
    [internalSize, compactLayout],
  );
  const buttonClass = useMemo(
    () => classNames('button-container', { [`button-container--${internalSize}`]: internalSize }),
    [internalSize],
  );

  useEffect(() => {
    if (containerSize === '' && menu.current) {
      const bounding = menu.current.getBoundingClientRect();
      setInternalSize(
        (getSmallestBreakpointForWidth(bounding?.width) as LabelsMenuProps['containerSize']) ?? '',
      );
    }
  }, [containerSize, menu]);

  /**
   * @returns {import('@/molecules').DropDownProps['menuItems']}
   */
  const teamFolderMenuItems = useCallback(() => {
    const folderMenuItems =
      teamFolders?.map((folder) => ({
        label: folder.name,
        value: folder.id,
      })) ?? null;
    folderMenuItems?.unshift({
      label: 'All folders',
      value: '',
    });
    return folderMenuItems ?? [];
  }, [teamFolders]);

  const teamFoldersDropdown = useMemo(() => {
    if (!teamFolders || teamFolders.length === 0) {
      return null;
    }
    return (
      <DropDown
        defaultValue="Select folder"
        menuItems={teamFolderMenuItems()}
        onChange={handleFoldersChange}
        size="small"
        className="team-folders-dropdown"
        isRebrand={isRebrand}
      />
    );
  }, [teamFolders, handleFoldersChange, teamFolderMenuItems, isRebrand]);

  return (
    <div
      className={classNames(
        className,
        { [`size--${internalSize}`]: internalSize, 'brand--new': isRebrand },
        `layout--${layout}`,
      )}
      data-testid="labels-dropdown"
    >
      <div
        ref={menu}
        className={classNames(
          'labels-container',
          { [overlayClasses]: !disableButtons },
          `layout--${layout}`,
        )}
      >
        <div
          className={classNames('dropdown', {
            [`dropdown--${internalSize}`]: internalSize,
            'large-display': !smallDisplay,
          })}
        >
          <DropDown
            defaultValue={defaultDropdownValue}
            menuItems={dropdownItems}
            onChange={handleDropdownItemChange}
            size="small"
            align="left"
            onOpen={onMenuOpen}
            className="labels-dropdown"
            isRebrand={isRebrand}
          />
          {/* Default view, large viewport */}
          {!compactLayout && !smallDisplay && teamFolders && (
            <div className="folders-dropdown">{teamFoldersDropdown}</div>
          )}
        </div>
        {!compactLayout && (
          <>
            <div className={`${buttonClass} button--preview`}>
              <Button
                isRebrand={isRebrand}
                rectangle={isRebrand}
                color={themeColor}
                label="Preview"
                onClick={onItemPreview}
                outline
                disabled={disableButtons}
                size="small"
              />
            </div>
            {showInsertButton && (
              <div className={`${buttonClass} button--insert`}>
                <Button
                  isRebrand={isRebrand}
                  rectangle={isRebrand}
                  color={themeColor}
                  label="Insert"
                  onClick={onItemSelect}
                  disabled={disableButtons || !canInsert}
                  size="small"
                />
              </div>
            )}
          </>
        )}
      </div>
      {loadingTeamFolders && (
        <div className="folders-dropdown">
          <LoadingDots />
        </div>
      )}
      {smallDisplay && teamFolders ? (
        <div className="folders-dropdown">{teamFoldersDropdown}</div>
      ) : null}
    </div>
  );
};

const fullSizeMargin = `${30 / 16}rem`;
const smallSizeMargin = `${20 / 16}rem`;

const styledLabelsMenu = styled(LabelsMenu)`
  border-bottom: 0.0625rem solid ${colors.whiteSmoke};
  padding: 1.4375rem ${fullSizeMargin};
  background-color: ${colors.white};
  z-index: 1;

  &.size--xsmall,
  &.size--chrome,
  &.size--mobile {
    padding: 1.4375rem ${smallSizeMargin};
  }

  .labels-container {
    align-items: stretch;
    display: flex;
    .dropdown--xsmall {
      max-width: 100%;
    }
  }

  .dropdown {
    flex: 1;
    max-width: 14.375rem;
    margin-right: auto;

    &--mobile,
    &--chrome,
    &--small,
    &--xsmall {
      .box {
        .text {
          margin-left: 0.5rem;
        }

        svg {
          margin-right: 0.5rem;
        }
      }
    }

    &--chrome {
      .box {
        max-width: 14.375rem;
      }
    }

    > div {
      flex: 1;
    }

    &.large-display {
      display: flex;
      > div:first-of-type {
        margin-right: 12px;
        max-width: 100%;
      }
      .folders-dropdown {
        margin-top: unset;
      }
    }
  }

  .folders-dropdown {
    margin-top: 10px;
  }

  .button-container {
    margin-left: 0.625rem;
    text-wrap: nowrap;

    button:disabled {
      cursor: default;
    }

    &:nth-of-type(2) {
      margin-left: 1.375rem;
    }

    button {
      width: 6.25rem;
    }

    /* Overrides Design Spec (Sarah approved) for smallest breakpoint */
    &--mobile,
    &--chrome,
    &--small,
    &--xsmall,
    &--w360 {
      &:nth-of-type(2) {
        margin-left: ${20 / 16}rem;
      }

      button[type='button'] {
        padding: 0 0.8125rem;
      }

      button {
        width: 4.64875rem;
      }
    }

    &--mobile::nth-of-type(2) {
      margin-left: ${10 / 16}rem;
    }
  }

  &.layout--compact .dropdown {
    max-width: 100%;

    &--mobile,
    &--chrome,
    &--small,
    &--xsmall {
      .box {
        max-width: 100%;

        .text {
          margin-left: 0.5rem;
        }

        svg {
          margin-right: 0.5rem;
        }
      }
    }
  }

  &.brand--new {
    &.library-header--labels {
      height: 100%;
      .labels-container,
      .labels-container .dropdown,
      .labels-container .dropdown .labels-dropdown > [role='button'] {
        height: 100%;
      }
    }
    .dropdown {
      .labels-dropdown {
        ${(props) => (props.isRebrand ? mediumInter() : semiBold())};
        color: ${(props) => (props.isRebrand ? colors.boulder : colors.battleship)};

        > [role='button'] {
          height: 100%;
        }
        .box {
          border-color: ${colors.whiteSmoke};
          border-width: 2px;
          border-radius: 3px;
          height: 100%;
        }
      }
    }

    .button-container {
      &.button--preview button {
        &:disabled {
          color: ${rebrandColors.disableGrey};
          border-color: ${rebrandColors.silver};
        }

        &:not(:disabled):hover {
          color: ${colors.white};
          background: ${rebrandColors.electricBlue};
          border-color: ${rebrandColors.electricBlue};
        }
      }

      &.button--insert button:disabled {
        color: ${colors.white};
        background: ${rebrandColors.disableGrey};
      }
    }
  }
`;

export default styledLabelsMenu;
